import { useApiDelete, useApiPost, useApiPut } from '@/api/main/query'
import {
  NewPaymentRequisiteModel,
  PaymentRequisiteModel,
  paymentRequisitsAPI,
} from '@/entities/paymentRequisit'
import { invalidateAllRequisiteData, requisiteAPI } from '@/features/requisites'
import { useToast } from '@/shared/hooks'
import { useQueryClient } from '@tanstack/react-query'
import isEqual from 'lodash.isequal'
import { RequisiteModel, UpdateRequisite } from '../model/types'
import { ApiError } from '@/api/types'
import { traderAPI } from '@/entities/trader'
import { apiDelete, apiPost } from '@/api/main/client.ts'
import { omit } from 'lodash'
import { useRef } from 'react'

export const useEditRequisit = (
  queryRequisitData?: RequisiteModel | null,
  id?: string,
  onSuccessCallback?: () => void
) => {
  const sendingRef = useRef(false);

  const { addToast } = useToast()
  const queryClient = useQueryClient()

  const handleSuccess = (message: string) => {
    addToast('Успех!', message, 'success')
    invalidateAllRequisiteData(queryClient, id)
    if (onSuccessCallback) onSuccessCallback()
  }

  const handleError = (error: ApiError) => {
    addToast('Ошибка!', error.message, 'error')
  }

  const { mutateAsync: updateRequisit } = useApiPut<RequisiteModel, UpdateRequisite>(
    requisiteAPI.updateRequisit,
    {
      onSuccess: () => handleSuccess('Реквизит обновлен'),
      onError: handleError,
    }
  )

  const { mutateAsync: createPaymentRequisit } = useApiPost<
    PaymentRequisiteModel,
    NewPaymentRequisiteModel
  >(paymentRequisitsAPI.create, {
    onSuccess: () => handleSuccess('Дополнительный метод оплаты создан'),
    onError: handleError,
  })

  const { mutate: deleteRequisit } = useApiDelete(requisiteAPI.deleteRequisit, {
    onSuccess: () => handleSuccess('Реквизит удалён'),
    onError: handleError,
  })

  const handleUpdateRequisitSubmit = async (data: UpdateRequisite) => {
    if (sendingRef.current) return;
    sendingRef.current = true;

      try {
        if (!queryRequisitData) {
          addToast('Ошибка!', 'Не удалось получить данные реквизита', 'error')
          return
        }

        // сравнение без paymentRequisitProfiles
        const restNew = omit(data, ['paymentRequisitProfiles']);
        const restOld = omit(queryRequisitData, ['paymentRequisitProfiles']);
        const isRequisitChanged = !isEqual(restOld, restNew);

        // вычисляем, какие профили реально новые (нет id и нет совпадения по типу+значению)
        const newProfiles = (data.paymentRequisitProfiles ?? []);
        const oldProfiles = (queryRequisitData.paymentRequisitProfiles ?? []);

        const isSame = (a: PaymentRequisiteModel, b: PaymentRequisiteModel) =>
          a.requisitType === b.requisitType && a.value === b.value;

        const createdProfiles = newProfiles.filter(np =>
          !np.id && !oldProfiles.some(op => isSame(op, np))
        );

        if (isRequisitChanged) {
            await updateRequisit(data)
        }

        // затем создаём НОВЫЕ платёжные профили (если есть)
        if (createdProfiles.length) {
          for (const p of createdProfiles) {
            if (p.value && p.requisitType) {
                const created = await createPaymentRequisit(
                  {
                    value: p.value,
                    requisitType: p.requisitType,
                    requisitId: data.id,
                  }
                )

                queryRequisitData.paymentRequisitProfiles ||= [];
                queryRequisitData.paymentRequisitProfiles.push(created);

                await new Promise(r => setTimeout(r, 500))
            }
          }
        }

        await paymentMethodLimitsHandler(
          data.paymentRequisitProfiles,
          queryRequisitData.paymentRequisitProfiles
        );
      } catch (e) {
        handleError(e as ApiError);
      } finally {
        sendingRef.current = false;
      }
  }

  const paymentMethodLimitsHandler = async (
    currentRequisites: PaymentRequisiteModel[] | null,
    oldRequisites: PaymentRequisiteModel[] | null,
  ) => {
    if (!currentRequisites || !oldRequisites || oldRequisites.length === 0) return;

    // из-за неверной работы getPaymentRequisitProfiles, приходится искать первый массив через данные которые пришли
    // в компонент, чтобы знать ид первого элемента
    const mainStateLimits = currentRequisites.find(r => r.id === oldRequisites[0].id)?.limits;

    if (!mainStateLimits) return

    const requests: Array<() => Promise<void>> = []

    for (const profile of oldRequisites ?? []) {
      for (const limit of mainStateLimits) {
        const create = !profile.limits.some(l => l.topupPaymentChannelId === limit.topupPaymentChannelId)
        const updateLimit = profile.limits
          .find(l => l.topupPaymentChannelId === limit.topupPaymentChannelId && l.maxPerDay !== limit.maxPerDay)

        if (create) {
            requests.push(() =>
            apiPost<void>(
              traderAPI.topups.limits.create(profile.id, limit.topupPaymentChannelId, Number(limit.maxPerDay)),
              {}
            ))
        }
        if (updateLimit) {
          requests.push(() =>
            apiDelete<void>(
              traderAPI.topups.limits.delete(updateLimit.id),
              {}
            ))
          requests.push(() =>
            apiPost<void>(
              traderAPI.topups.limits.create(profile.id, limit.topupPaymentChannelId, Number(limit.maxPerDay)),
              {}
            ))
        }
      }
      for (const limit of profile.limits) {
        const drop = !mainStateLimits.some(l => l.topupPaymentChannelId === limit.topupPaymentChannelId)
        if (!drop) continue
        requests.push(() =>
          apiDelete<void>(
            traderAPI.topups.limits.delete(limit.id),
            {}
          )
        )
      }
    }

    for (const run of requests) {
      await run()
      await new Promise(r => setTimeout(r, 500))
    }

    if (requests.length) {
      handleSuccess('Лимиты по платёжным методам обновлены')
    }
  }

  const handleDeleteRequisit = () => {
    deleteRequisit(id ?? '')
  }

  return { handleUpdateRequisitSubmit, handleDeleteRequisit }
}
